/**
 * Power Pages domain models.
 *
 * The backend returns snake_case; these interfaces describe the camelCase
 * shape after `transformSnakeToCamel` is applied at the service boundary.
 */

export enum PowerPageStatus {
  Published = 'active',
  Draft = 'inactive',
}

export interface PowerPageOwner {
  id?: string | number;
  uuid?: string;
  firstName?: string;
  lastName?: string;
}

export interface PowerPageCreator {
  id?: string | number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PowerPageSetting {
  key?: string;
  value?: string | null;
}

export interface PowerPage {
  uuid: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  url?: string | null;
  status: PowerPageStatus | string;
  settings?: PowerPageSetting[];
  owner?: PowerPageOwner | null;
  creator?: PowerPageCreator | null;
  clicks?: number | null;
  totalLinks?: number | null;
  isAdmin?: boolean;
  isManager?: boolean;
}

export interface PowerPagesListMeta {
  currentPage: number;
  perPage: number;
  total: number;
  totalPages?: number;
}

export interface PowerPagesListResponse {
  data: PowerPage[];
  meta: PowerPagesListMeta;
}

export interface PowerPagesListParams {
  page?: number;
  perPage?: number;
  search?: string;
  sort?: string;
  sortDirection?: string;
}

export const DEFAULT_POWER_PAGES_CURRENT_PAGE = 1;
export const DEFAULT_POWER_PAGES_PER_PAGE = 24;
export const POWER_PAGES_PAGE_SIZE_OPTIONS = [DEFAULT_POWER_PAGES_PER_PAGE, 48, 96] as const;

/* -------------------------------------------------------------------------- */
/* Editor (create/edit) models                                                */
/* -------------------------------------------------------------------------- */

/** A single option consumed by Kendo dropdown/multiselect (valuePrimitive). */
export interface PowerPageSelectOption {
  label: string;
  value: string;
}

/** Manage-tab options returned by GET /{domainId}/power-pages/manage. */
export interface PowerPageManageOptions {
  domain?: { name?: string; uuid?: string } | null;
  admins?: Array<{ id?: string | number; firstName?: string; lastName?: string }>;
  managers?: Array<{ id?: string | number; firstName?: string; lastName?: string }>;
  socialMedia?: Array<{ id?: string; code?: string; name?: string }>;
}

/** A social media entry attached to a power page. */
export interface PowerPageSocialMedia {
  id?: string;
  code?: string;
  /** Display/name of the social network (React `socialMedia`/`name`). */
  socialMedia?: string;
  /** Target URL. */
  url?: string;
  link?: string;
}

/** A single API setting entry (0-indexed array element, snake at the wire). */
export interface PowerPageApiSetting {
  name: string;
  uuid?: string;
  value: string;
  isEnabled?: boolean;
  isMarkup?: boolean;
  image?: { uuid?: string; url?: string } | null;
}

/** A power page with the full detail payload used by the editor (edit mode). */
export interface PowerPageDetail extends PowerPage {
  path?: string | null;
  domain?: { name?: string; uuid?: string } | null;
  settings?: PowerPageApiSetting[];
  socialMedia?: PowerPageSocialMedia[];
  users?: Array<{
    id?: string | number;
    firstName?: string;
    lastName?: string;
    roles?: Array<{ code?: string }>;
  }>;
  isAdmin?: boolean;
  isManager?: boolean;
}

/** Payload sent to create/update a power page (camelCase; service snakes it). */
export interface PowerPageSavePayload {
  uuid?: string;
  status: PowerPageStatus | string;
  path: string;
  name: string;
  description?: string;
  domainUuid?: string;
  owner?: string;
  powerPageAdmins?: string[];
  contentManager?: string[];
  settings: PowerPageApiSetting[];
  socialMedias?: PowerPageSocialMedia[];
}

/* -------------------------------------------------------------------------- */
/* Content blocks (Manage Block / Content tab)                                 */
/* -------------------------------------------------------------------------- */

/**
 * The link attached to a content block. This is a loose shape of the bl.ink
 * link as returned by the power-page-blocks endpoint; only the fields the
 * editor reads are typed, the rest are preserved via index signature.
 */
export interface PowerPageBlockLink {
  id?: number | string;
  uuid?: string;
  alias?: string;
  longUrl?: string;
  redirectType?: string | number;
  ownerId?: string | number;
  groups?: Array<{ uuid: string; name?: string }>;
  tags?: Array<{ uuid: string; name?: string }>;
  assignedAttributes?: unknown[];
  archivedAt?: string | null;
  deletedAt?: string | null;
  [key: string]: unknown;
}

/** Per-block display settings held by the editor (camelCase FE shape). */
export interface PowerPageBlockSettings {
  uuid?: string;
  imageUuid?: string;
  /** Persisted background image uuid/url, or a pending File during editing. */
  background?: string | File | null;
  /** Object/remote URL used for previewing the background. */
  backgroundUrl?: string;
  publishDate?: string;
  retireDate?: string;
  text?: string;
  textColor?: string;
  index?: number;
}

/** A content block as used by the editor. */
export interface PowerPageBlock {
  link: PowerPageBlockLink;
  blockSettings: PowerPageBlockSettings;
}

/**
 * The block_settings portion of the create/edit block request body
 * (snake_case at the wire; built explicitly in the component).
 */
export interface PowerPageBlockSettingsPayload {
  background: string | null;
  published_at: string;
  retired_at: string;
  title: string;
  text_color: string;
}

/** Body for create/edit block: { blocks: { link, block_settings } }. */
export interface PowerPageBlockSavePayload {
  link: Record<string, unknown>;
  blockSettings: PowerPageBlockSettingsPayload;
}

/** Result of a trusted-destination check. */
export interface TrustedDestinationCheckResult {
  isTrusted: boolean;
  status?: string;
  matchedPattern?: string | null;
}
